import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import _ from 'lodash';
import {
  useInfiniteStudentsQuery,
  studentQueryKeys,
  useTotalTeacherListQuery,
} from '@apis/query/user';
import { getStudentsInfoByIds } from '@apis/api/student/common';
import { StudentStatus } from '@asset/enum/UserEnum';
import { FilterListOptionType } from '@asset/type/common/tableList';

export const useInfiniteStudentFilterList = (
  defaultStudentIds: number[],
  studentQueryParameters?: {
    pageSize?: number;
    status?: StudentStatus;
    teacherIds?: number[];
    search?: string;
  },
): {
  studentList;
  fetchNextStudentList: () => void;
  hasNextStudentList: boolean;
  isStudentsLoading: boolean;
} => {
  const {
    data: infiniteStudentsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteStudentsQuery(studentQueryParameters);

  const loadedStudents = useMemo(
    () => infiniteStudentsData?.pages.flatMap((page) => page.students) ?? [],
    [infiniteStudentsData],
  );

  const loadedStudentIds = useMemo(
    () => new Set(loadedStudents.map((student) => student.id)),
    [loadedStudents],
  );

  const hasMissingDefaultStudents = useMemo(
    () => defaultStudentIds.some((id) => !loadedStudentIds.has(id)),
    [defaultStudentIds, loadedStudentIds],
  );

  const { data: defaultStudents } = useQuery(
    studentQueryKeys.detailByIds(defaultStudentIds),
    () => getStudentsInfoByIds(defaultStudentIds),
    {
      enabled: hasMissingDefaultStudents,
    },
  );

  const studentList = useMemo(() => {
    const formattedDefaultStudents = (defaultStudents || []).map((student) => ({
      id: student.id,
      name: student.name,
    }));
    const combinedList = [...formattedDefaultStudents, ...loadedStudents];
    return _.uniqBy(combinedList, 'id');
  }, [defaultStudents, loadedStudents]);

  return {
    studentList,
    fetchNextStudentList: fetchNextPage,
    hasNextStudentList: hasNextPage,
    isStudentsLoading: isFetchingNextPage,
  };
};

export const useStudentFilterForCommonList = (options?: {
  pageSize?: number;
  status?: StudentStatus;
  teacherIds?: number[];
  defaultStudentIds?: number[];
}): {
  optionList: FilterListOptionType[];
  onChangeSearchValue: (searchValue: string) => void;
  filterInfiniteScroll: {
    onLoadNextPage: () => void;
    hasMore: boolean;
    isLoading: boolean;
  };
} => {
  const [searchValue, setSearchValue] = useState<string>('');

  const {
    studentList,
    fetchNextStudentList,
    hasNextStudentList,
    isStudentsLoading,
  } = useInfiniteStudentFilterList(options?.defaultStudentIds ?? [], {
    pageSize: options?.pageSize ?? 100,
    status: options?.status ?? StudentStatus.ENROLLED,
    teacherIds: options?.teacherIds,
    search: searchValue !== '' ? searchValue : null,
  });

  return {
    optionList: studentList.map((student) => ({
      value: student.id,
      label: student.name,
    })),
    onChangeSearchValue: setSearchValue,
    filterInfiniteScroll: {
      onLoadNextPage: fetchNextStudentList,
      hasMore: hasNextStudentList,
      isLoading: isStudentsLoading,
    },
  };
};

export const useTeacherFilterForCommonList = (): {
  optionList: FilterListOptionType[];
} => {
  const { data: teacherList } = useTotalTeacherListQuery();

  const optionList = useMemo(() => {
    return teacherList
      ? teacherList.map((student) => ({
          value: student.id,
          label: student.name,
        }))
      : [];
  }, [teacherList]);

  return {
    optionList,
  };
};
